import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	useWaterAccounts,
	WaterAccountSelector,
} from "@/shared/selectors/WaterAccountSelector";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Button, Grid2, MenuItem, Paper, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAsInteger, useQueryState } from "nuqs";
import { useState } from "react";
import type { WaterBillDraft, WaterBillsData } from "../models/WaterBilling";
import {
	deleteWaterBill,
	getWaterBills,
	upsertWaterBill,
} from "../repositories/WaterBillingRepository";
import type { WaterBillGridRow } from "../utils/billRows";
import {
	buildWaterBillDraft,
	buildWaterBillGridRows,
	getWaterBillPeriods,
} from "../utils/billRows";
import { NewWaterBillDialog } from "./NewWaterBillDialog";
import { WaterBillsTable } from "./table/WaterBillsTable";

const moment = getMomentTz();

const billYears = () => {
	const currentYear = new Date().getFullYear();
	return Array.from({ length: 6 }, (_, index) => currentYear - index);
};

export const useWaterBills = ({
	waterAccountId,
	year,
}: {
	waterAccountId: number;
	year: number | null;
}) => {
	const startDate = year ? moment().year(year).startOf("year").toDate() : null;
	const endDate = year ? moment().year(year).endOf("year").toDate() : null;

	return useQuery<WaterBillsData, Error>({
		queryKey: ["water-bills", waterAccountId, year],
		queryFn: ({ signal }) =>
			getWaterBills({ waterAccountId, startDate, endDate }, signal),
		enabled: waterAccountId !== null,
		staleTime: 60_000,
	});
};

export const WaterBills = () => {
	const queryClient = useQueryClient();
	const [waterAccountId, setWaterAccountId] = useQueryState(
		"account",
		parseAsInteger,
	);

	const [year, setYear] = useState<number | null>(new Date().getFullYear());
	const [isNewBillOpen, setIsNewBillOpen] = useState(false);
	const [billToDelete, setBillToDelete] = useState<number | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const {
		data,
		isFetching,
		isError,
		error: e,
	} = useWaterBills({ waterAccountId: waterAccountId!, year });
	const { data: waterAccountsData, isLoading: isWaterAccountsLoading } =
		useWaterAccounts();

	const account =
		waterAccountsData?.accounts.find(
			(account) => account.WaterAccountId === waterAccountId,
		) ?? null;
	const accountMeters = (waterAccountsData?.meters ?? []).filter(
		(meter) => meter.WaterAccountId === waterAccountId,
	);
	const rows = data ? buildWaterBillGridRows(data) : [];

	const targets = (data?.targets ?? []).filter((target) => target.IsVisible);
	const targetCodes = targets.map((target) => target.TargetCode);

	const periods = data ? getWaterBillPeriods(data) : [];

	const saveBill = useMutation(upsertWaterBill, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["water-bills", waterAccountId],
			});
			setNotificationMessage("The bill was saved successfully");
		},
		onError: async (e: Error) => {
			await queryClient.invalidateQueries({
				queryKey: ["water-bills", waterAccountId],
			});
			setError(e.message);
		},
	});

	const removeBill = useMutation(deleteWaterBill, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["water-bills", waterAccountId],
			});
			queryClient.invalidateQueries({ queryKey: ["water-accounts"] });
			setNotificationMessage("The bill was deleted successfully");
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const handleBillSave = (billRows: WaterBillGridRow[]) => {
		if (waterAccountId === null || billRows.length === 0) return;
		saveBill.mutate(buildWaterBillDraft(waterAccountId, billRows, targetCodes));
	};

	const handleCreate = async (period: {
		ServicePeriodStart: Date;
		ServicePeriodEnd: Date;
	}) => {
		if (waterAccountId === null) return false;

		const draft: WaterBillDraft = {
			BillId: null,
			WaterAccountId: waterAccountId,
			ServicePeriodStart: period.ServicePeriodStart,
			ServicePeriodEnd: period.ServicePeriodEnd,
			BalanceForward: null,
			CurrentBill: null,
			CheckNumber: null,
			PayDate: null,
			Meters: accountMeters.map((meter) => ({
				MeterId: meter.MeterId,
				Gallons: null,
				StartReadType: null,
				EndReadType: null,
			})),
			Allocations: null,
		};

		const created = await saveBill
			.mutateAsync(draft)
			.then(() => true)
			.catch(() => false);

		if (created)
			await queryClient.invalidateQueries({ queryKey: ["water-accounts"] });

		return created;
	};

	return (
		<>
			<Paper component="section" elevation={2} sx={{ mb: 1, p: 1 }}>
				<Grid2 container spacing={1.5} alignItems="center">
					<Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
						<WaterAccountSelector
							value={waterAccountId}
							onChange={setWaterAccountId}
						/>
					</Grid2>
					<Grid2 size={{ xs: 6, sm: 3, md: 1.5 }}>
						<TextField
							select
							fullWidth
							size="small"
							label="Year"
							value={year ?? "all"}
							onChange={(event) =>
								setYear(
									event.target.value === "all"
										? null
										: Number(event.target.value),
								)
							}
						>
							<MenuItem value={"all"}>All</MenuItem>
							{billYears().map((billYear) => (
								<MenuItem key={billYear} value={billYear}>
									{billYear}
								</MenuItem>
							))}
						</TextField>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
						<TextField
							fullWidth
							size="small"
							label="Location"
							value={account?.Location ?? ""}
							InputProps={{ readOnly: true }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: "auto" }} sx={{ ml: { md: "auto" } }}>
						<Button
							variant="contained"
							size="small"
							startIcon={<Add />}
							disabled={waterAccountId === null || accountMeters.length === 0}
							onClick={() => setIsNewBillOpen(true)}
						>
							New bill
						</Button>
					</Grid2>
				</Grid2>
			</Paper>
			<WaterBillsTable
				rows={rows}
				targets={targets}
				periods={periods}
				isLoading={isFetching || saveBill.isPending || removeBill.isPending}
				onBillSave={handleBillSave}
				onBillDelete={setBillToDelete}
			/>
			<NewWaterBillDialog
				open={isNewBillOpen}
				accountLabel={account?.AccountNumber ?? ""}
				meterCount={accountMeters.length}
				periods={periods}
				isSaving={saveBill.isPending}
				onClose={() => setIsNewBillOpen(false)}
				onCreate={handleCreate}
			/>
			<ConfirmationDialogRaw
				open={billToDelete !== null}
				title="Delete bill"
				message="The bill, its meter lines and its split will be removed. This cannot be undone."
				onClose={(value?: string) => {
					if (value === "OK" && billToDelete !== null) {
						removeBill.mutate(billToDelete);
					}
					setBillToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
